import { Col, Row } from 'reactstrap';
import React, { useCallback, useState } from 'react';
import { useApiGet } from 'hooks/useApi';
import Pagination from 'models/Pagination';
import PaginationNav from 'components/Pagination';
import { Link, useNavigate } from 'react-router-dom';
import Icon from 'components/Icon';
import Table from 'components/Table';
import { Column } from 'components/Table/_presenters/props/tableComponentProps';
import License from 'models/License';
import { LicenseTypeLabel } from 'enum/LicenseType';
import moment from 'moment';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import Customer from 'models/Customer';

type FilteredData = {
  pageSize: number;
  page: number;
  customer?: string;
};

const AdminLicensesListPage = () => {
  const navigate = useNavigate();
  const [customersSearch, setCustomersSearch] = useState('');
  const [typeaheadKey, setTypeaheadKey] = useState(0);
  const [filteredData, setFilteredData] = React.useState<FilteredData>({
    pageSize: 10,
    page: 1
  });
  const { data: licences, isLoading } = useApiGet<Pagination<License>>(
    '/admin/licenses',
    {
      params: filteredData
    }
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setFilteredData({ ...filteredData, page });
    },
    [filteredData]
  );

  const goToEdit = useCallback(
    (license: License) => {
      navigate(`/admin/licenses/${license.id}`);
    },
    [navigate]
  );

  const { data: customers, isLoading: isLoadingCustomers } = useApiGet<
    Pagination<Customer>
  >(
    '/admin/customers',
    customersSearch
      ? {
          params: {
            search: customersSearch
          }
        }
      : undefined
  );

  const columns: Column<License>[] = [
    {
      accessor: 'customer' as keyof License,
      label: 'Cliente',
      render: (_: any, license: License) => (
        <span className="fw-medium">{license?.customer?.name || '-'}</span>
      )
    },
    {
      accessor: 'licenseType',
      label: 'Tipo',
      render: (type) => (
        <span>{LicenseTypeLabel[type as keyof typeof LicenseTypeLabel]}</span>
      )
    },
    {
      accessor: 'active',
      label: 'Status',
      render: (active) => (
        <span className={`badge ${active ? 'bg-success' : 'bg-secondary'}`}>
          {active ? 'Ativa' : 'Inativa'}
        </span>
      )
    },
    {
      accessor: 'expirationDate',
      label: 'Expiração',
      render: (date) => (
        <span>{date ? moment(String(date)).format('L') : '-'}</span>
      )
    },
    {
      accessor: 'actions',
      label: 'Ações',
      render: () => <Icon icon="eye" className="text-primary" />
    }
  ];

  return (
    <div className="padding-page">
      <Row className="d-flex flex-column flex-md-row">
        <Col className="col-12 col-md">
          <div>
            <p className="text-gray-600">Administração</p>
            <h4 className="fw-bold">Licenças</h4>
            <span className="small-typography text-gray-600">
              Gerencie as licenças dos clientes da plataforma.
            </span>
          </div>
        </Col>
        <Col className="col-12 col-md align-text mt-3 mt-md-0">
          <Link
            to="/admin/licenses/create"
            className="btn btn-primary text-white button-register"
          >
            Cadastrar
          </Link>
        </Col>
      </Row>
      <Row className="d-flex align-items-center mt-2 flex-column flex-md-row">
        <Col className="col-12 col-md-4">
          <AsyncTypeahead
            key={typeaheadKey}
            options={customers?.items || []}
            delay={500}
            onSearch={(query) => setCustomersSearch(query)}
            labelKey={'name'}
            clearButton
            placeholder="Filtrar por cliente"
            isLoading={isLoadingCustomers}
            onChange={(selected) => {
              if (selected && selected.length) {
                const customer = selected[0] as Customer;
                setFilteredData({
                  ...filteredData,
                  page: 1,
                  customer: customer.id
                });
              } else {
                setCustomersSearch('');
                setFilteredData({
                  ...filteredData,
                  page: 1,
                  customer: undefined
                });
                setTypeaheadKey((k) => k + 1);
              }
            }}
          />
        </Col>
      </Row>
      <Table
        data={licences?.items || []}
        columns={columns}
        isLoading={isLoading}
        onRowClick={goToEdit}
      />
      <div className="d-flex justify-content-between align-items-center flex-column flex-md-row mt-3">
        {licences && (
          <PaginationNav
            onChange={handlePageChange}
            totalPages={licences.totalPages}
            currentPage={licences.page}
          />
        )}
      </div>
    </div>
  );
};

export default AdminLicensesListPage;
